import { KeyValuePair, StringKeyDictionary } from "../commonInterfaces.js";
import { Metadata } from "../core/components/commonApp.js";
import { Config } from "../core/config/config.js";
import { DialogHelper } from "../core/util/dialogHelper.js";
import { Helper } from "../core/util/helper.js";
import { HTTPService, JsonResultClass } from "../core/util/httpManager.js";
import { NotificationHelper } from "../core/util/notificationHelper.js";
import { AssignmentDistributionInput, AssignmentDistributionInputEntity, AssignmentDistributionInputProps } from "./forms/assignmentDistributionInput.js";
import { AssignmentDistributionPreviewProps, PreviewEntity, AssignmentDistributionPreview } from "./forms/assignmentDistributionPreview.js";
import { DistributionInfo, ResultPreview } from "./forms/resultPreview.js";
import React from "react"
export enum WizardStage {
    Input = 0,
    Preview = 1,
    ResultPreview = 2,
    Apply = 3
}

export default class AssignmentDistributionWizard extends React.Component<AssignmentDistributionWizardProps, AssignmentDistributionWizardState>{
    private assignmentDistributionInputRef: AssignmentDistributionInput;
    private translations: StringKeyDictionary<string> = null;
    constructor(props: AssignmentDistributionWizardProps) {
        super(props);
        this.state = {
            inputEntity: props.inputEntity,
            previewEntity: null,
            stage: WizardStage.Input,
            resultPreviewEntity: null,
            applyEnabled: false
        };
        this.loadTranslations();
    }

    private loadTranslations = async () => {
        const result = await Helper.getTranslations([
            "Standard.Übernehmen", "WebSMS.DistributeAssignments.OpenResourcePlanning", "Standard.Continue",
            "WebSMS.DistributeAssignments.BackToInput", "Standard.BackBtn", "Standard.Übernehmen", "WebSMS.DistributeAssignments.Apply"
        ]);
        this.translations = result.Data;
        this.setState({})
    }

    private onPrevClick = async () => {
        if (this.state.stage === WizardStage.Apply) {
            this.setState({ stage: WizardStage.Input })
        } else {
            this.setState({ stage: this.state.stage - 1 });
        }
    }

    private onNextClick = async () => {
        let ok = true;
        switch (this.state.stage) {
            case WizardStage.Input:
                ok = await this.generatePreview();
                break;
            case WizardStage.Preview:
                ok = await this.distribute();
                break;
            case WizardStage.ResultPreview:
                ok = await this.applyDistribution();
                break;
            case WizardStage.Apply:
                ok = false;
                Helper.openInNewBrowserTab(`${Config.baseUrl}ResourcePlanningScheduler`);
                break;
        }
        if (ok) {
            this.setState({ stage: this.state.stage + 1 });
        }
    }

    private generatePreview = async () => {
        const isValid = await this.assignmentDistributionInputRef.validate();
        if (!isValid) {
            return false;
        }
        const result = await HTTPService.post<JsonResultClass<PreviewEntity>>("AssignmentDistribution/CreatePreview", { criteria: this.state.inputEntity });
        if (result.Code == 1) {
            NotificationHelper.showErrorMessage(result.Message)
        } else {
            this.setState({ previewEntity: result.Data })
        }
        return result.Code === 0;
    }

    private applyDistribution = async () => {
        const isOk = await DialogHelper.confirm(Helper.getTranslation("WebSMS.DistributeAssignments.RUSure"));
        if (!isOk)
            return false;
        const result = await HTTPService.post<JsonResultClass<PreviewEntity>>("AssignmentDistribution/ApplyDistribution", { criteria: this.state.inputEntity, strategy: this.state.inputEntity.ActiveStrategy });
        if (result.Code == 1) {
            NotificationHelper.showErrorMessage(result.Message)
        }
        return result.Code === 0;
    }

    private distribute = async () => {
        const result = await HTTPService.post<JsonResultClass<DistributionInfo>>("AssignmentDistribution/DistributeAssignments", { criteria: this.state.inputEntity, strategy: this.state.inputEntity.ActiveStrategy });
        if (result.Code == 1) {
            NotificationHelper.showErrorMessage(result.Message)
        } else {
            this.setState({
                resultPreviewEntity: result.Data,
                applyEnabled: result.AdditionalData["applyEnabled"]
            })
        }
        return result.Code === 0;
    }

    private renderContent = () => {
        switch (this.state.stage) {
            case WizardStage.Input:
                return this.renderInput();
            case WizardStage.Preview:
                return this.renderPreview();
            case WizardStage.ResultPreview:
                return this.renderResultPreview();
            case WizardStage.Apply:
                NotificationHelper.showSuccessMessage(Helper.getTranslation("WebSMS.DistributeAssignments.SuccessfullyDistributed"));
                return this.renderResultPreview();
        }
    }

    private renderInput = () => {
        const props: AssignmentDistributionInputProps = {
            context: {
                contextType: "assignmentDistributionInput"
            },
            dataSources: this.props.dataSources,
            entity: this.state.inputEntity,
            isWindow: true,
            metadata: this.props.inputMetaData,
            onChange: this.onInputChange,
            title: "",
        }
        return (
            <>
                <AssignmentDistributionInput {...props} ref={(ref) => {
                    this.assignmentDistributionInputRef = ref;
                }} />
            </>
        );
    }

    private renderPreview = () => {
        const props: AssignmentDistributionPreviewProps = {
            context: {
                contextType: "assignmentDistributionPreview"
            },
            dataSources: {},
            entity: this.state.previewEntity,
            isWindow: true,
            metadata: this.props.previewMetaData,
            title: "",
        }
        return (
            <>
                <AssignmentDistributionPreview {...props} />
            </>
        );
    }

    private renderResultPreview = () => {
        return (
            <>
                <ResultPreview info={this.state.resultPreviewEntity} />
            </>
        );
    }

    private onInputChange = (entity: AssignmentDistributionInputEntity) => {
        this.setState({ inputEntity: entity })
    }

    private isNextEnabled = () => {
        if (this.state.stage === WizardStage.ResultPreview)
            return this.state.applyEnabled;
        if (this.state.stage === WizardStage.Preview)
            return this.state.previewEntity.Assignments.filter(a => a.IsValid).length > 0 &&
                this.state.previewEntity.Resources.filter(r => r.IsValid).length > 0;
        return true;
    }

    private getNextLabel = () => {
        switch (this.state.stage) {
            case WizardStage.ResultPreview:
                return this.translations["WebSMS.DistributeAssignments.Apply"];
            case WizardStage.Apply:
                return this.translations["WebSMS.DistributeAssignments.OpenResourcePlanning"];
            default:
                return this.translations["Standard.Continue"];
        }
    }

    render() {
        if (!Helper.hasValue(this.translations)) {
            return null;
        }
        return (
            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Header stage={this.state.stage} />
                <div style={{ flex: "1", height: "100%" }}>
                    {this.renderContent()}
                </div>
                <Footer
                    onPrevClick={this.onPrevClick}
                    onNextClick={this.onNextClick}

                    nextEnabled={this.isNextEnabled()}
                    prevEnabled={this.state.stage !== WizardStage.Input}

                    nextLabel={this.getNextLabel()}
                    prevLabel={this.state.stage === WizardStage.Apply ? this.translations["WebSMS.DistributeAssignments.BackToInput"] : this.translations["Standard.BackBtn"]}
                />
            </div>
        );
    }
}

export interface AssignmentDistributionWizardProps {
    inputEntity: AssignmentDistributionInputEntity;
    inputMetaData: Metadata;
    previewMetaData: Metadata;
    dataSources: {
        strategies: KeyValuePair<string, string>[];
    }
}

interface AssignmentDistributionWizardState {
    stage: WizardStage;
    inputEntity: AssignmentDistributionInputEntity;
    previewEntity: PreviewEntity;
    resultPreviewEntity: DistributionInfo;
    applyEnabled: boolean;
}

class Header extends React.Component<HeaderProps, any>{
    private names = {
        0: Helper.getTranslation("WebSMS.DistributeAssignments.Input"),
        1: Helper.getTranslation("Standard.Preview"),
        2: Helper.getTranslation("WebSMS.DistributeAssignments.ResultPreview"),
        3: Helper.getTranslation("WebCRM.Tasks.ExecuteAll.Result")
    }

    private getCSSClass = (stage: WizardStage) => {
        const className = "assignmentDistribution__header__item";
        if (this.props.stage === stage) {
            return `${className}--active`;
        } else if (stage < this.props.stage) {
            return `${className}--completed`;
        } else {
            return `${className}--inactive`;
        }
    }

    private renderHeader = (stage: WizardStage) => {
        return (
            <div className={"assignmentDistribution__header__item " + this.getCSSClass(stage)}>
                <label style={{ fontSize: "15px", margin: "0 5px" }}>{this.names[stage]}</label>
            </div>);
    }

    render() {

        return (
            <>
                <div className="assignmentDistribution__header">
                    {this.renderHeader(WizardStage.Input)}
                    {this.renderHeader(WizardStage.Preview)}
                    {this.renderHeader(WizardStage.ResultPreview)}
                    {this.renderHeader(WizardStage.Apply)}
                </div>
            </>
        );
    }
}

interface HeaderProps {
    stage: WizardStage;
}

class Footer extends React.Component<FooterProps, any>{

    private renderButton = (onClick: () => Promise<void>, enabled: boolean, text: string) => {
        return (
            <div onClick={enabled ? onClick : () => { }} className={"assignmentDistribution__footer__item assignmentDistribution__footer__item" + (enabled ? "--active" : "--inactive")}>
                <label style={{ fontSize: "15px", margin: "0 5px", cursor: enabled ? "pointer" : "auto" }}>
                    {text}
                </label>
            </div>
        );
    }

    render() {
        return (
            <div>
                <div style={{ display: "flex", justifyContent: "space-between", width: "50%" }}>
                    {this.renderButton(this.props.onPrevClick, this.props.prevEnabled, this.props.prevLabel)}
                    {this.renderButton(this.props.onNextClick, this.props.nextEnabled, this.props.nextLabel)}
                </div>
            </div>
        );
    }
}

interface FooterProps {
    onPrevClick: () => Promise<void>;
    onNextClick: () => Promise<void>;

    prevEnabled: boolean;
    nextEnabled: boolean;

    nextLabel: string;
    prevLabel: string;
}
